import { useState, useEffect, useContext } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import blackDeleteIcon from "../../assets/black-delete-icon.svg";
import whiteRightIcon from "../../assets/white-right-icon.svg";
import editIcon from "../../assets/edit-latest-icon.svg";
import saveIcon from "../../assets/save-icon.svg";
import { CaseStageContext } from "../../dashboard/cases/contexts/CaseContexts";
import { CaseServices } from "../../services/CaseServices";
import { HospitalServices } from "../../services/HospitalServices";
import { handleApiErrorResponses } from "../../utils/ApisErrorHandling";
import { useLocation } from "react-router-dom";

const doctorSelectFont = {
  fontFamily: "Lufga, sans-serif",
  fontSize: "14px",
  letterSpacing: "-0.03em",
};

const doctorSelectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    ...doctorSelectFont,
    minHeight: "36px",
    height: "36px",
    borderRadius: "6px",
    borderColor: isFocused ? "#3B82F6" : "#000000",
    backgroundColor: "#FFFFFF",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: isFocused ? "#3B82F6" : "#000000" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px" }),
  placeholder: (base) => ({ ...base, ...doctorSelectFont, color: "#9CA3AF" }),
  singleValue: (base) => ({ ...base, ...doctorSelectFont, color: "#000000" }),
  input: (base) => ({
    ...base,
    ...doctorSelectFont,
    color: "#000000",
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "#E5E7EB",
    marginTop: "8px",
    marginBottom: "8px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#374151",
    padding: "0 8px",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "6px",
    overflow: "hidden",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
    marginTop: "4px",
    zIndex: 10,
  }),
  menuList: (base) => ({ ...base, padding: "4px 0", maxHeight: "300px" }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    ...doctorSelectFont,
    color: "#000000",
    padding: "10px 12px",
    cursor: "pointer",
    backgroundColor: isSelected || isFocused ? "#EBF5FF" : "#FFFFFF",
    "&:active": { backgroundColor: "#EBF5FF" },
  }),
};

const doctorSelectProps = {
  getOptionLabel: (o) =>
    o.id === "other" ? "Other" : `${o.fullName}-${o.highestQualification}`,
  getOptionValue: (o) => (o.id === "other" ? "other" : o.id),
  placeholder: "Select Doctor",
  styles: doctorSelectStyles,
  classNamePrefix: "care-team-doctor-select",
  menuPlacement: "bottom",
  hideSelectedOptions: false,
  isSearchable: true,
};

const otherDoctorOption = {
  id: "other",
  fullName: "Other",
  highestQualification: "",
};

const emptyAddDoctorForm = {
  doctorsName: "",
  registrationHPRID: "",
  contactNumber: "",
  qualification: "",
};

// Shared input styling used by every editable cell (populated rows,
// the empty-state row, and the "Add new doctor" form row).
const rowInputClassName =
  "w-full bg-transparent rounded-[6px] px-[6px] py-[4px] font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000] border border-black outline-none";

const gridColsClass =
  "grid grid-cols-[0.70fr_2.44fr_1.64fr_3.14fr_1.39fr_0.70fr] gap-x-[84px] 3xl:gap-x-[140px] items-start";

const sanitizeField = (field, value) => {
  if (field === "registrationHPRID")
    return value.replace(/[^a-zA-Z0-9\s]/g, "");
  if (field === "qualification") return value.replace(/[^a-zA-Z\s,]/g, "");
  if (field === "contactNumber")
    return value.replace(/[^0-9]/g, "").slice(0, 10);
  return value;
};

const CareTeamDetailsForm = ({ data }) => {
  const { caseDetails, patientDetails, fetchCaseDetails } =
    useContext(CaseStageContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDoctors, setEditDoctors] = useState(data || []);
  const [dropdownDoctors, setDropdownDoctors] = useState([]);
  const [addDoctorRowIndex, setAddDoctorRowIndex] = useState(null);
  const [addDoctorForm, setAddDoctorForm] = useState(emptyAddDoctorForm);
  const location = useLocation();
  const caseProcedures = caseDetails?.caseprocedures || [];
  const savedDoctors = caseProcedures.flatMap((procedure) => {
    const details = procedure.doctorDetails || [];
    return details.map((doc, idx) => ({
      ...doc,
      registrationHPRID: doc.registrationHPRID || doc.registrationNumber || "",
      qualification: (doc.qualification || "").replace(/[^a-zA-Z\s,]/g, ""),
      ...(idx === 0 && procedure.doctorId
        ? { procedureDoctorId: procedure.doctorId }
        : {}),
    }));
  });
  const careTeamList = isEditMode ? editDoctors : savedDoctors;

  useEffect(() => {
    if (isEditMode) return;
    setEditDoctors(savedDoctors);
  }, [caseDetails, isEditMode]);

  useEffect(() => {
    if (!patientDetails?.hospitalId) return;

    const getDoctorList = async () => {
      try {
        const response = await HospitalServices.getDoctorList(
          "",
          1,
          40,
          patientDetails.hospitalId,
        );
        setDropdownDoctors(response.data.list);
      } catch (error) {
        handleApiErrorResponses(error);
      }
    };

    getDoctorList();
  }, [patientDetails?.hospitalId]);

  // Builds the updated caseprocedures payload from the edited doctor rows.
  // Existing procedure.doctorDetails slots get updated in place. If there
  // are more rows in `doctors` than existing slots (e.g. the care team
  // started empty and a doctor was just added), the leftover rows are
  // appended onto the first procedure instead of being silently dropped.
  const getUpdatedProcedures = (doctors) => {
    let index = 0;

    const built = caseProcedures.map((procedure) => {
      const existingDoctors = procedure.doctorDetails || [];
      let doctorName = procedure.doctorName;
      let doctorId = procedure.doctorId;

      const doctorDetails = existingDoctors.map((existing, i) => {
        const row = doctors[index] || existing;
        index += 1;

        if (i === 0) {
          doctorName = row.doctorsName;
          doctorId = row.procedureDoctorId || procedure.doctorId;
        }

        return {
          ...existing,
          doctorsName: row.doctorsName,
          registrationHPRID: row.registrationHPRID,
          qualification: row.qualification,
          contactNumber: row.contactNumber,
        };
      });

      return { procedure, doctorName, doctorId, doctorDetails };
    });

    // Append any leftover new rows (typically the very first doctor ever
    // added when doctorDetails started out empty) onto the first procedure.
    if (index < doctors.length && built.length > 0) {
      const first = built[0];
      for (; index < doctors.length; index++) {
        const row = doctors[index];
        if (first.doctorDetails.length === 0) {
          first.doctorName = row.doctorsName;
          first.doctorId = row.procedureDoctorId || first.doctorId;
        }
        first.doctorDetails.push({
          doctorsName: row.doctorsName,
          registrationHPRID: row.registrationHPRID,
          qualification: row.qualification,
          contactNumber: row.contactNumber,
        });
      }
    }

    return built.map(({ procedure, doctorName, doctorId, doctorDetails }) => ({
      ...procedure,
      doctorName,
      doctorId,
      doctorDetails,
    }));
  };

  const persistDoctors = async (doctors) => {
    await CaseServices.updateCaseProcedures(
      getUpdatedProcedures(doctors),
      caseDetails.id,
    );
    await fetchCaseDetails?.();
  };

  const onInputChange = (index, field, value) => {
    const list = [...editDoctors];
    list[index] = { ...list[index], [field]: sanitizeField(field, value) };
    setEditDoctors(list);
  };

  const onDoctorChange = (index, doctor) => {
    if (!doctor) return;

    if (doctor.id === "other") {
      setAddDoctorRowIndex(index);
      setAddDoctorForm(emptyAddDoctorForm);
      return;
    }

    setAddDoctorRowIndex(null);
    setAddDoctorForm(emptyAddDoctorForm);

    const list = [...editDoctors];
    list[index] = {
      ...list[index],
      doctorsName: doctor.fullName,
      procedureDoctorId: doctor.doctorhospitalmappings?.[0]?.doctorId || "",
      qualification: sanitizeField(
        "qualification",
        doctor.highestQualification || "",
      ),
      registrationHPRID: sanitizeField(
        "registrationHPRID",
        doctor.registrationHPRID || doctor.registrationNumber || "",
      ),
      contactNumber: sanitizeField("contactNumber", doctor.phone || ""),
    };
    setEditDoctors(list);
  };

  const onAddDoctorFieldChange = (field, value) => {
    setAddDoctorForm({
      ...addDoctorForm,
      [field]: sanitizeField(field, value),
    });
  };

  const saveNewDoctor = async () => {
    const name = addDoctorForm.doctorsName.trim();
    const phone = addDoctorForm.contactNumber;

    if (!name) {
      toast.error("Doctor name is required");
      return;
    }

    if (phone && phone.length !== 10) {
      toast.error("Contact number must be 10 digits");
      return;
    }

    if (!patientDetails?.hospitalId || !caseDetails?.id) return;

    const response = await HospitalServices.addDoctor({
      fullName: name,
      registrationHPRID: addDoctorForm.registrationHPRID,
      registrationNumber: addDoctorForm.registrationHPRID,
      phone,
      highestQualification: addDoctorForm.qualification,
      hospitalId: patientDetails.hospitalId,
      gender: "OTHER",
      doctorType: "Others",
      specialityIds: [],
    });

    const { data } = await HospitalServices.getDoctorList(
      "",
      1,
      40,
      patientDetails.hospitalId,
    );
    const doctorOptions = data.list || [];
    setDropdownDoctors(doctorOptions);

    const newDoctor = doctorOptions.find((item) => item.fullName === name);
    // If we're adding the very first doctor (empty care-team state),
    // addDoctorRowIndex is 0 but editDoctors is still []; fall back to 0.
    const rowIndex = addDoctorRowIndex ?? 0;
    const doctors = [...editDoctors];

    doctors[rowIndex] = {
      doctorsName: name,
      registrationHPRID: addDoctorForm.registrationHPRID,
      contactNumber: phone,
      qualification: addDoctorForm.qualification,
      procedureDoctorId: newDoctor?.doctorhospitalmappings?.[0]?.doctorId || "",
    };

    setEditDoctors(doctors);
    setAddDoctorRowIndex(null);
    setAddDoctorForm(emptyAddDoctorForm);

    await persistDoctors(doctors);

    if (response.data.message) {
      toast.success(response.data.message);
    }
  };

  const handleSave = async () => {
    try {
      if (addDoctorRowIndex !== null) {
        await saveNewDoctor();
        return;
      }

      const updatedIds = [];

      for (let i = 0; i < editDoctors.length; i++) {
        const edited = editDoctors[i];
        const original = savedDoctors[i];
        if (!original) continue;

        const hasChanges =
          edited.registrationHPRID != original.registrationHPRID ||
          edited.qualification != original.qualification ||
          edited.contactNumber != original.contactNumber;

        if (!hasChanges) continue;

        const doctor = dropdownDoctors.find(
          (item) => item.fullName === edited.doctorsName,
        );

        if (!doctor?.id || updatedIds.includes(doctor.id)) continue;

        updatedIds.push(doctor.id);

        await HospitalServices.updateCareTeamDoctorDetail({
          doctorId: doctor.id,
          registrationHPRID: edited.registrationHPRID ?? null,
          highestQualification: edited.qualification ?? null,
          phone: edited.contactNumber ?? null,
        });
      }

      const response = await CaseServices.updateCaseProcedures(
        getUpdatedProcedures(editDoctors),
        caseDetails.id,
      );

      if (patientDetails?.hospitalId) {
        const { data } = await HospitalServices.getDoctorList(
          "",
          1,
          40,
          patientDetails.hospitalId,
        );
        setDropdownDoctors(data.list);
      }

      await fetchCaseDetails?.();
      setAddDoctorRowIndex(null);
      setIsEditMode(false);

      if (response.data.message) {
        toast.success(response.data.message);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="pt-[30px]">
        {/* Table Header */}
        <div className={`${gridColsClass} items-start`}>
          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            No.
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Doctor Name
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F] whitespace-nowrap">
            Registration ID
          </div>

          <div className="3xl:pl-10 font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Qualification
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F] whitespace-nowrap">
            Contact Number
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Action
          </div>
        </div>

        {/* Table Body */}
        <div className="mt-[34px] flex flex-col gap-y-[34px] ">
          {careTeamList?.length > 0
            ? careTeamList.map((item, index) => (
                <div key={index}>
                  <div className={gridColsClass}>
                    {/* Number */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                      {index + 1}.
                    </div>

                    {/* Doctor Name */}
                    {isEditMode && (
                      <div className="relative overflow-visible">
                        <Select
                          {...doctorSelectProps}
                          options={dropdownDoctors.concat(otherDoctorOption)}
                          value={
                            addDoctorRowIndex === index
                              ? otherDoctorOption
                              : dropdownDoctors.find(
                                  (option) =>
                                    option.fullName === item?.doctorsName,
                                ) || null
                          }
                          onChange={(doctor) => onDoctorChange(index, doctor)}
                        />
                      </div>
                    )}
                    {!isEditMode && (
                      <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000] whitespace-nowrap">
                        {item?.doctorsName || "--"}
                      </div>
                    )}

                    {/* Registration ID */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                      {!isEditMode && (item?.registrationHPRID || "--")}
                      {isEditMode && addDoctorRowIndex !== index && (
                        <input
                          type="text"
                          value={item?.registrationHPRID || ""}
                          onChange={(e) =>
                            onInputChange(
                              index,
                              "registrationHPRID",
                              e.target.value,
                            )
                          }
                          className={rowInputClassName}
                        />
                      )}
                    </div>

                    {/* Qualification */}
                    <div className="flex items-center gap-[6px]">
                      {!isEditMode && (
                        <span className="font-lufga font-normal text-[14px] pl-11 leading-[100%] tracking-[-0.03em] text-[#000000]">
                          {item?.qualification || "--"}
                        </span>
                      )}
                      {isEditMode && addDoctorRowIndex !== index && (
                        <input
                          type="text"
                          value={item.qualification || ""}
                          pattern="[A-Za-z\s,]*"
                          onChange={(e) =>
                            onInputChange(
                              index,
                              "qualification",
                              e.target.value,
                            )
                          }
                          className={`${rowInputClassName} pl-11`}
                        />
                      )}
                    </div>

                    {/* Contact Number */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000] whitespace-nowrap">
                      {!isEditMode && (item?.contactNumber || "--")}
                      {isEditMode && addDoctorRowIndex !== index && (
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={10}
                          value={item.contactNumber || ""}
                          onChange={(e) =>
                            onInputChange(
                              index,
                              "contactNumber",
                              e.target.value,
                            )
                          }
                          className={rowInputClassName}
                        />
                      )}
                    </div>

                    {/* Delete */}
                    <button className="flex items-center justify-self-end bg-transparent border-none">
                      <img src={blackDeleteIcon} alt="delete icon" />
                    </button>
                  </div>

                  {isEditMode && addDoctorRowIndex === index && (
                    <div className={`mt-[16px] ${gridColsClass}`}>
                      <div />
                      <input
                        type="text"
                        placeholder="Doctor Name"
                        value={addDoctorForm.doctorsName}
                        onChange={(e) =>
                          onAddDoctorFieldChange("doctorsName", e.target.value)
                        }
                        className={rowInputClassName}
                      />
                      <input
                        type="text"
                        placeholder="Registration ID"
                        value={addDoctorForm.registrationHPRID}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "registrationHPRID",
                            e.target.value,
                          )
                        }
                        className={rowInputClassName}
                      />
                      <input
                        type="text"
                        placeholder="Qualification"
                        pattern="[A-Za-z\s,]*"
                        value={addDoctorForm.qualification}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "qualification",
                            e.target.value,
                          )
                        }
                        className={`${rowInputClassName} pl-11`}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="Contact Number"
                        value={addDoctorForm.contactNumber}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "contactNumber",
                            e.target.value,
                          )
                        }
                        className={rowInputClassName}
                      />
                      <div />
                    </div>
                  )}
                </div>
              ))
            : // Empty state: nothing saved yet. When in edit mode, show a
              // single "add doctor" row (index 0) using the same
              // dropdown/"Other" flow as the populated case, so the user can
              // add their very first care-team doctor.
              isEditMode && (
                <div>
                  <div className={gridColsClass}>
                    {/* Number */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                      1.
                    </div>

                    {/* Doctor Name */}
                    <div className="relative overflow-visible">
                      <Select
                        {...doctorSelectProps}
                        options={dropdownDoctors.concat(otherDoctorOption)}
                        value={
                          addDoctorRowIndex === 0
                            ? otherDoctorOption
                            : dropdownDoctors.find(
                                (option) =>
                                  option.fullName ===
                                  editDoctors[0]?.doctorsName,
                              ) || null
                        }
                        onChange={(doctor) => onDoctorChange(0, doctor)}
                      />
                    </div>

                    {/* Registration ID */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                      {addDoctorRowIndex !== 0 && (
                        <input
                          type="text"
                          value={editDoctors[0]?.registrationHPRID || ""}
                          onChange={(e) =>
                            onInputChange(
                              0,
                              "registrationHPRID",
                              e.target.value,
                            )
                          }
                          className={rowInputClassName}
                        />
                      )}
                    </div>

                    {/* Qualification */}
                    <div className="flex items-center gap-[6px]">
                      {addDoctorRowIndex !== 0 && (
                        <input
                          type="text"
                          value={editDoctors[0]?.qualification || ""}
                          pattern="[A-Za-z\s,]*"
                          onChange={(e) =>
                            onInputChange(0, "qualification", e.target.value)
                          }
                          className={`${rowInputClassName} pl-11`}
                        />
                      )}
                    </div>

                    {/* Contact Number */}
                    <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000] whitespace-nowrap">
                      {addDoctorRowIndex !== 0 && (
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={10}
                          value={editDoctors[0]?.contactNumber || ""}
                          onChange={(e) =>
                            onInputChange(0, "contactNumber", e.target.value)
                          }
                          className={rowInputClassName}
                        />
                      )}
                    </div>

                    {/* Delete (kept for grid alignment; not functional on an
                      unsaved empty-state row) */}
                    <button className="flex items-center justify-self-end bg-transparent border-none">
                      <img src={blackDeleteIcon} alt="delete icon" />
                    </button>
                  </div>

                  {addDoctorRowIndex === 0 && (
                    <div className={`mt-[16px] ${gridColsClass}`}>
                      <div />
                      <input
                        type="text"
                        placeholder="Doctor Name"
                        value={addDoctorForm.doctorsName}
                        onChange={(e) =>
                          onAddDoctorFieldChange("doctorsName", e.target.value)
                        }
                        className={rowInputClassName}
                      />
                      <input
                        type="text"
                        placeholder="Registration ID"
                        value={addDoctorForm.registrationHPRID}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "registrationHPRID",
                            e.target.value,
                          )
                        }
                        className={rowInputClassName}
                      />
                      <input
                        type="text"
                        placeholder="Qualification"
                        pattern="[A-Za-z\s,]*"
                        value={addDoctorForm.qualification}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "qualification",
                            e.target.value,
                          )
                        }
                        className={`${rowInputClassName} pl-11`}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="Contact Number"
                        value={addDoctorForm.contactNumber}
                        onChange={(e) =>
                          onAddDoctorFieldChange(
                            "contactNumber",
                            e.target.value,
                          )
                        }
                        className={rowInputClassName}
                      />
                      <div />
                    </div>
                  )}
                </div>
              )}
        </div>
      </div>
      {/* Save Section */}
      {!location?.pathname?.toLowerCase()?.includes("editcases") && (
        <div className="flex justify-end items-center gap-[10px] mt-[40px]">
          {isEditMode ? (
            <button
              onClick={handleSave}
              className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px] 2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal"
            >
              SAVE
              <img src={saveIcon} alt="save" className="pl-[16px]" />
            </button>
          ) : (
            <button className="w-[170px] h-[74px] xl:w-[101px] xl:h-[42px] 2xl:w-[101px] 2xl:h-[42px] rounded-full border border-none bg-[#5AC69F] flex justify-center items-center gap-[10px] font-lufga text-[14px] text-white font-normal">
              SAVED
              <img src={whiteRightIcon} alt="white icon" />
            </button>
          )}

          {!isEditMode && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setEditDoctors(savedDoctors);
                setAddDoctorRowIndex(null);
                setIsEditMode(true);
              }}
              className="w-[42px] h-[42px] rounded-full border border-solid border-[#5AC69F] flex justify-center items-center bg-transparent"
            >
              <img src={editIcon} alt="edit icon" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CareTeamDetailsForm;
